import React from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Dialog,
  TextField,
  Button,
  DialogActions,
  DialogTitle,
  DialogContent,
  useTheme,
  useMediaQuery,
  DialogContentText,
} from "@mui/material";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import axios from "../../utils/axios.tsx";
import { conversationActions } from "../../store/conversation.tsx";
import { useDispatch } from "react-redux";
import { useTypedSelector } from "../../hooks.tsx";
import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";
import { Trans } from "@lingui/react/macro";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

interface ThumbDownPromptProps {
  exchange_id: string | undefined;
  clicked: boolean | false;
}

const ThumbDownPrompt: React.FC<ThumbDownPromptProps> = (props) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const [feedback, setFeedback] = React.useState("");
  const anonymousSession = useTypedSelector(
    (state) => state.authState.anonymousSession,
  );
  const selectedConversationId = useTypedSelector(
    (state) => state.conversationState.selectedConversation,
  );

  const handleFeedbackOpen = () => setFeedbackOpen(true);
  const handleFeedbackClose = () => setFeedbackOpen(false);
  const handleSubmit = async () => {
    const slug = anonymousSession
      ? "/v1/anonymous/bad-response/"
      : "/v1/bad-response/";
    await axios
      .post(
        slug,
        JSON.stringify({
          exchange_id: props.exchange_id,
          user_comment: feedback,
        }),
      )
      .then(() => {
        dispatch(
          conversationActions.updateExchangeById({
            id: props.exchange_id,
            updateFields: { feedback: "bad", user_comment: feedback },
            conversationId: selectedConversationId,
          }),
        );
      });
    handleFeedbackClose();
  };

  return (
    <Box>
      <Tooltip
        title={i18n._(
          msg({
            message: "Bad Response",
            context: "Tooltip label for the bad response button.",
          }),
        )}
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              fontSize: "0.9rem",
            },
          },
        }}
      >
        <span>
          <IconButton
            onClick={handleFeedbackOpen}
            size="small"
            disabled={props.clicked}
          >
            {props.clicked ? (
              <ThumbDownAltIcon
                fontSize="small"
                style={{ color: theme.palette.primary.main }}
              /> // Filled icon if clicked is true
            ) : (
              <ThumbDownAltOutlinedIcon fontSize="small" /> // Outlined icon otherwise
            )}
          </IconButton>
        </span>
      </Tooltip>
      {/* Dialog */}
      <Dialog
        open={feedbackOpen}
        onClose={handleFeedbackClose}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: theme.spacing(2),
            marginLeft: useMediaQuery(theme.breakpoints.up("md"))
              ? `calc(${SETTINGS.drawerWidth}px)`
              : "auto",
            paddingBottom: theme.spacing(2.5),
          },
        }}
      >
        <DialogTitle
          sx={{
            color: theme.palette.primary.main,
          }}
        >
          <Trans
            context={
              "Dialog title enabling the user to submit feedback on why the user"
            }
          >
            Tell me more
          </Trans>
        </DialogTitle>
        <DialogContentText
          sx={{
            color: theme.palette.text.disabled,
            paddingLeft: theme.spacing(3),
          }}
        >
          <Trans
            context={
              "A dialog message asking the user to elaborate on why this response is not good."
            }
          >
            Please tell me what is the problem with this response.
          </Trans>
        </DialogContentText>
        <DialogContent>
          <TextField
            fullWidth
            variant="filled"
            multiline
            rows={6}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            InputProps={{
              style: {
                borderRadius: theme.spacing(2),
                paddingBottom: theme.spacing(2.5),
              },
              disableUnderline: true,
            }}
          />
        </DialogContent>

        {/* Dialog Actions: Submit Button */}
        <DialogActions
          sx={{
            marginRight: theme.spacing(2),
          }}
        >
          <Button
            variant="outlined"
            onClick={handleFeedbackClose}
            sx={{
              borderRadius: theme.spacing(2),
            }}
          >
            <Trans context={"Title for the cancel button"}>Cancel</Trans>
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            sx={{ fontWeight: "bold", borderRadius: theme.spacing(2) }}
          >
            <Trans context={"Title for the submit button"}>Submit</Trans>
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ThumbDownPrompt;
